import { useCallback, useEffect, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";

import { GET_CUSTOMER_FILTERS } from "../graphql/query";
import {
  CustomerDataEntry,
  SiteDataEntry,
  WasteTypeDataEntry,
} from "../utils/interfaces";
import * as Storage from "../storage";

// Requests already on the way, keyed by customer id, so two pages or two
// effects asking for the same customer share one call.
const pendingFilters = new Map<string, Promise<CustomerDataEntry>>();

// Maps the lightweight getCustomers result to the customer selector entries.
export const customerListFormat = (customers: any[]): CustomerDataEntry[] =>
  (customers ?? []).map((elem: any) => ({
    value: elem.id,
    label: elem.customer,
    category: elem.category,
  }));

// Loads sites and waste types for one customer on demand and stores them in
// USER_DATA, so the SPs run once for the selected customer only.
export const useCustomerFilters = () => {
  const client = useApolloClient();

  return useCallback(
    async (customer: CustomerDataEntry): Promise<CustomerDataEntry> => {
      if (customer.sites && customer.wastes) return customer;

      const key = String(customer.value);
      const cachedList = await Storage.USER_DATA.get();
      const cached = cachedList.find((c) => String(c.value) === key);
      if (cached?.sites && cached?.wastes)
        return { ...customer, sites: cached.sites, wastes: cached.wastes };

      let request = pendingFilters.get(key);
      if (!request) {
        request = client
          .query({
            query: GET_CUSTOMER_FILTERS,
            variables: { customerId: customer.value },
            context: { apiName: "middleware" },
            fetchPolicy: "no-cache",
          })
          .then(async (res) => {
            const data = res.data?.getCustomers?.[0];
            const sites: SiteDataEntry[] = (data?.sites ?? []).map(
              (site: any) => ({
                value: parseInt(site.id),
                label: site.customerSite,
              })
            );
            const wastes: WasteTypeDataEntry[] = (data?.wastes ?? []).map(
              (wt: any) => ({
                value: parseInt(wt.wasteTypeId),
                label: wt.wasteType,
              })
            );
            if (data) {
              const list = await Storage.USER_DATA.get();
              const idx = list.findIndex((c) => String(c.value) === key);
              if (idx >= 0) {
                list[idx] = { ...list[idx], sites, wastes };
                await Storage.USER_DATA.set(list);
              }
            }
            return { ...customer, sites, wastes };
          })
          .catch((err) => {
            console.error(err);
            return { ...customer, sites: [], wastes: [] };
          })
          .finally(() => pendingFilters.delete(key));
        pendingFilters.set(key, request);
      }

      const loaded = await request;
      return { ...customer, sites: loaded.sites, wastes: loaded.wastes };
    },
    [client]
  );
};

// Returns false when called again with the same variables as the last call,
// so effects that re-run with unchanged inputs do not repeat the same SP call.
export const useRequestGuard = () => {
  const lastKey = useRef<string>();
  return useCallback((variables: unknown) => {
    const key = JSON.stringify(variables);
    if (key === lastKey.current) return false;
    lastKey.current = key;
    return true;
  }, []);
};

// Customer selector state built from the user info that DashboardLayout
// already loaded, so pages do not query every customer again.
export const useCustomerSelection = (userCustomers?: any[]) => {
  const [customerList, setCustomerList] = useState<CustomerDataEntry[]>([]);
  const [selectedCustomer, setSelectedCustomer] =
    useState<CustomerDataEntry>();

  useEffect(() => {
    if (!userCustomers || userCustomers.length === 0) return;
    const list = customerListFormat(userCustomers);
    setCustomerList(list);
    Storage.FILTER_DATA.get().then((filter) => {
      const idx = filter.customerIndex >= 0 ? filter.customerIndex : 0;
      setSelectedCustomer(list[idx] ?? list[0]);
    });
  }, [userCustomers]);

  return { customerList, selectedCustomer, setSelectedCustomer };
};
